import React, { useRef, useState, useEffect } from 'react'
import Measure from 'react-measure'

import { useForTablet, DisableBodyScroll } from '@sketch/components'
import { useOnClickOutside } from '@sketch/utils'

import { callIfFunction, RenderFunctionOrNode } from '../utils'

import {
  PageLayout,
  ContentContainer,
  SidebarLeftContainer,
  RightSidebarContainer,
  SidebarPlaceholder,
  SidebarBackdrop,
} from './SidebarLayout.styles'

/**
 * TYPES
 */
interface SidebarLayoutProps {
  title?: string
  /** Navigation sidebar */
  sidebarLeft?: RenderFunctionOrNode
  /** Activity/inspector sidebar */
  sidebarRight?: RenderFunctionOrNode
  header: RenderFunctionOrNode
  footer?: RenderFunctionOrNode
  children?: RenderFunctionOrNode
  isSidebarRightOpen?: boolean
  toggleSidebarRight?: () => void
  headerHeight?: number
  darkBackground?: boolean
}

const KEYS_ALLOW_ACTION = ['enter', 'space']

const getSidebarWidth = (width: number | 'auto', isOpen: boolean) =>
  width === 'auto' || isOpen ? width : 0

/**
 * COMPONENT
 * Used for document/artboard view, it provides a right sidebar to show
 * activity/inspector, also provides the left sidebar for nav (as an overlay)
 */
const SidebarLayout: React.FC<SidebarLayoutProps> = props => {
  const {
    title,
    sidebarLeft,
    sidebarRight,
    header,
    footer,
    toggleSidebarRight,
    children,
    darkBackground,
  } = props

  // If there's no way to render the right sidebar (i.e. it's null), then
  // ensure it can never be set as open
  const isSidebarRightOpen =
    (props.sidebarRight === null ? false : props.isSidebarRightOpen) || false

  const [isSidebarLeftOpen, setSidebarLeftOpen] = useState(false)
  const [sidebarWidth, setSidebarWidth] = useState(
    getSidebarWidth('auto', isSidebarRightOpen)
  )

  const passedProps = {
    isSidebarRightOpen,
    toggleSidebarRight,
    isSidebarLeftOpen,
    setSidebarLeftOpen,
  }

  const isTabletAndBigger = useForTablet()
  const isSmallerThanTablet = !isTabletAndBigger

  // Close the left sidebar when is opened and clicked outside
  const sidebarLeftRef = useRef<HTMLElement>(null)

  // Adds the `inert` html attribute to left sidebar when it's hidden, this way
  // make the browser ignore user input events for the element, including focus
  // events and events from assistive technologies.
  // Note: we need to do it this way because React still doesn't accept this attr
  useEffect(() => {
    if (!sidebarLeftRef.current) {
      return
    }

    if (isSidebarLeftOpen) {
      sidebarLeftRef.current.removeAttribute('inert')
    } else {
      sidebarLeftRef.current.setAttribute('inert', '')
    }
  }, [isSidebarLeftOpen])

  useOnClickOutside(sidebarLeftRef, () => {
    if (isSidebarLeftOpen) {
      // setTimeout is needed to make sure `setSidebarLeftOpen` is called after
      // any other click event listener, for example the hamburger icon click
      setTimeout(() => setSidebarLeftOpen(false), 0)
    }
  })

  return (
    <PageLayout
      title={title}
      isSidebarRightOpen={isSidebarRightOpen}
      header={callIfFunction(header, passedProps)}
      $darkBackground={darkBackground}
    >
      {sidebarLeft && (
        <SidebarLeftContainer
          data-testid="side-bar-left"
          ref={sidebarLeftRef}
          isSidebarLeftOpen={isSidebarLeftOpen}
        >
          {sidebarLeft}
        </SidebarLeftContainer>
      )}

      <ContentContainer>
        {callIfFunction(children, passedProps)}
        {callIfFunction(footer, passedProps)}
      </ContentContainer>

      {sidebarRight && (
        <Measure
          onResize={({ entry }) => {
            entry && setSidebarWidth(entry?.width)
          }}
        >
          {({ measureRef }) => (
            <>
              <RightSidebarContainer
                data-testid="side-bar-right"
                aria-hidden={!isSidebarRightOpen}
                ref={measureRef}
              >
                {callIfFunction(sidebarRight, passedProps)}
              </RightSidebarContainer>

              {!isSmallerThanTablet && (
                <SidebarPlaceholder
                  style={{
                    width: getSidebarWidth(sidebarWidth, isSidebarRightOpen),
                  }}
                />
              )}

              {isSmallerThanTablet && (
                <SidebarBackdrop
                  tabIndex={0}
                  onKeyPress={({ key }) =>
                    KEYS_ALLOW_ACTION.includes(key) && toggleSidebarRight?.()
                  }
                  onClick={() => toggleSidebarRight?.()}
                >
                  <span className="sr-only">Sidebar backdrop close</span>
                </SidebarBackdrop>
              )}
            </>
          )}
        </Measure>
      )}

      {isSmallerThanTablet && isSidebarRightOpen && <DisableBodyScroll />}
    </PageLayout>
  )
}

export { SidebarLayout }
